import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsRecommendedPackagePaymentStatusCheckInProgress } from '../../../../../../../selectors/depositAccount';
import { setSelectedRecommendedPackageId } from '../../../../../../../actions/recommendedPackages';
import { selectIsMobileLayout } from '../../../../../../../selectors/layout';
import { IRecommendedPackage } from '../../../../../../../types/reducers/recommendedPackages';
import { useAppContext } from '../../../../../../../../AppModulesWrapper';
import { useRecommendedPackageTrackers } from './useRecommendedPackageTrackers';
import { buyPackageThunk } from '../../../../../../../thunks/recommendedPackages/buyPackage';
import { buyPackageNativeThunk } from '../../../../../../../thunks/recommendedPackages/buyPackageNative';
import {
  selectDefaultRecommendedPackagePublicationPrice,
  selectIsRecommendedPackagesAvailable,
  selectRecommendedPackages,
} from '../../../../../../../reducers/v2/recommendedPackages/selectors';

export const useRecommendedPackages = () => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobileLayout);
  const { config, custom } = useAppContext();
  const isNativeCheckoutEnabled = !!config.get('servicePickerWidget.WebView.isNativeCheckoutEnabled');

  const { trackOnRecommendedPackageClick, trackOnShowRecommendedPackagesWidget, trackOnDefaultPublicationClick } =
    useRecommendedPackageTrackers();

  const isPaymentStatusCheckInProgress = useSelector(selectIsRecommendedPackagePaymentStatusCheckInProgress);

  const isRecommendedPackagesAvailable = useSelector(selectIsRecommendedPackagesAvailable);
  const isRender = !isPaymentStatusCheckInProgress && isRecommendedPackagesAvailable;

  const recommendedPackages = useSelector(selectRecommendedPackages);
  const offerPrice = useSelector(selectDefaultRecommendedPackagePublicationPrice);

  const handleDefaultPublicationClick = useCallback(() => {
    dispatch(setSelectedRecommendedPackageId(null));

    trackOnDefaultPublicationClick();
  }, [dispatch, trackOnDefaultPublicationClick]);

  const handleRecommendedPackageClick = useCallback(
    (recommendedPackage: IRecommendedPackage) => {
      trackOnRecommendedPackageClick(recommendedPackage);

      dispatch(setSelectedRecommendedPackageId(recommendedPackage.id));

      if (isNativeCheckoutEnabled && custom.useAppsCheckout) {
        dispatch(buyPackageNativeThunk());
      } else {
        dispatch(buyPackageThunk());
      }
    },
    [custom.useAppsCheckout, dispatch, isNativeCheckoutEnabled, trackOnRecommendedPackageClick],
  );

  useEffect(() => {
    if (isRender) {
      trackOnShowRecommendedPackagesWidget();
    }
  }, [isRender, trackOnShowRecommendedPackagesWidget]);

  return {
    isRender,
    isMobile,
    offerPrice,
    recommendedPackages,
    handleDefaultPublicationClick,
    handleRecommendedPackageClick,
  };
};
